import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, ArrowRight, Clock, Check, Warning, Lock, FileText, CaretRight, Brain, Barbell, Heart, Target, Tree, Compass, Plus, Lightning, Eye, Star } from '@phosphor-icons/react';
import Feld1Overlay from './components/Feld1Overlay';
import PhasenScan from './components/PhasenScan';
import KompassAssessment from './components/KompassAssessment';
import DeepeningQuestion from './components/DeepeningQuestion';
import VoiceTextarea from './components/VoiceTextarea';
import axios from 'axios';
import PhysicalAssessment from './components/PhysicalAssessment';
import CharacterAssessment from './components/CharacterAssessment';
import BiographicalDeepDive from './components/BiographicalDeepDive';
import HiddenTalentScan from './components/HiddenTalentScan';
import InstinctProfile from './components/InstinctProfile';
import StrategicProjection from './components/StrategicProjection';
import ProjectionPage from './components/ProjectionPage';
import { ThemeProvider, AmbientOrbs, ThemeToggle, useTheme } from './components/ThemeProvider';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Produktionssicherer Check: Dev-/Test-Bypass-Buttons nur außerhalb der Produktion zeigen
const IS_PRODUCTION = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production';

// Block 0 — separate, hervorgehoben
const BLOCK_ZERO = { id: 'hidden_talent', name: 'BLOCK 0', subtitle: 'THE RAW TRUTH', icon: Lightning, description: '11 Quick-Fire-Fragen. Deine unkonditionierten Instinkte, bevor die Gesellschaft dich geformt hat.' };

// Block configuration for Dashboard (Blocks 1-4)
const BLOCKS = {
  geist: { id: 'geist', name: 'GEIST', subtitle: 'MENTALE ANALYSE', icon: Brain, description: 'Kognitive Faehigkeiten, Denkweise, Entscheidungsmuster' },
  koerper: { id: 'koerper', name: 'KOERPER', subtitle: 'PHYSISCHE PRAESENZ', icon: Barbell, description: 'Koerperbewusstsein, Energie, physische Reaktionen' },
  wesen: { id: 'wesen', name: 'WESEN', subtitle: 'CHARAKTER-KERN', icon: Heart, description: 'Werte, Authentizitaet, emotionale Tiefe' },
  biografie: { id: 'biografie', name: 'BIOGRAFIE', subtitle: 'IKIGAI-ARCHAEOLOGIE', icon: Tree, description: 'Herkunft, Praegung, Leidenschaft, Flow-Momente' },
  kompass: { id: 'kompass', name: 'KOMPASS', subtitle: 'INNERER NAVIGATOR', icon: Compass, description: 'Werte, Risiko-Profil, Autoritaet, Eigenverantwortung' }
};

// ==================== COMPONENTS ====================

const NoiseOverlay = () => <div className="noise-overlay" />;

const SkillBar = ({ label, value, delay = 0 }) => {
  const [width, setWidth] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setWidth(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  // Color based on score
  const getColor = () => {
    if (value >= 75) return '#22C55E'; // Green
    if (value >= 50) return '#EAB308'; // Yellow
    if (value >= 25) return '#F97316'; // Orange
    return '#EF4444'; // Red
  };

  return (
    <div className="skill-bar">
      <div 
        className="skill-bar-fill" 
        style={{ width: `${width}%`, backgroundColor: getColor() }}
      />
      <span className="skill-bar-label">{label.replace(/_/g, ' ').toUpperCase()}</span>
      <span className="skill-bar-value">{value}%</span>
    </div>
  );
};

const LoadingScreen = ({ text = "LADE DATEN..." }) => (
  <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg-void)' }}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center"
    >
      <Fingerprint size={64} className="mx-auto mb-8 opacity-50" />
      <div className="loading-bar w-64 mb-4">
        <div className="loading-bar-fill" />
      </div>
      <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>{text}</p>
    </motion.div>
  </div>
);

const AgeCard = ({ group, isSelected, onClick }) => (
  <motion.div
    whileHover={{ y: -4, boxShadow: isSelected ? '0 12px 40px rgba(91,184,196,0.2)' : '0 12px 40px #CBD5E1' }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="cursor-pointer h-full flex flex-col p-6"
    style={{
      background: 'var(--bg-surface)',
      border: `1.5px solid ${isSelected ? 'var(--accent-cyan)' : 'var(--border-default)'}`,
      borderRadius: 'var(--radius-md)',
      backdropFilter: 'var(--glass-blur)',
      boxShadow: isSelected ? '0 8px 30px rgba(91,184,196,0.18)' : 'var(--card-shadow)',
      transition: 'all 0.3s ease'
    }}
    data-testid={`age-card-${group.id}`}
  >
    <span className="text-overline mb-2">{group.age_range} Jahre</span>
    <h3 className="text-2xl font-bold mb-3" style={{ lineHeight: 1 }}>{group.name}</h3>
    <p className="text-sm mb-4 font-medium" style={{ color: 'var(--accent-cyan)' }}>{group.tagline}</p>
    <p className="text-sm flex-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
      {group.description}
    </p>
    {isSelected && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 flex items-center gap-2" style={{ color: 'var(--accent-cyan)' }}>
        <Check size={20} weight="bold" />
        <span className="text-sm font-semibold">Ausgewaehlt</span>
      </motion.div>
    )}
  </motion.div>
);

// ==================== PAGES ====================

// ==================== RESUME MODAL ====================

const ResumeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('input'); // input | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [debugToken, setDebugToken] = useState(null);
  const [subjectId, setSubjectId] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setErrorMsg('');
    setPhase('input');

    try {
      const response = await axios.post(`${API}/auth/request-access`, { identifier: identifier.trim() });
      setSubjectId(response.data.subject_id || '');
      setDebugToken(response.data._debug_token || null);
      setPhase('success');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Keine Session mit dieser Identität gefunden.');
      setPhase('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectAccess = async () => {
    if (!debugToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/verify-token?token=${debugToken}`);
      if (response.data.valid) {
        navigate(`/dashboard/${response.data.session_id}`);
      }
    } catch (err) {
      setErrorMsg('Verifizierung fehlgeschlagen.');
      setPhase('error');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setIdentifier('');
    setPhase('input');
    setErrorMsg('');
    setDebugToken(null);
    setSubjectId('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(16px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) { onClose(); resetModal(); } }}
        data-testid="resume-modal-overlay"
      >
        <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-lg relative"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '24px', backdropFilter: 'blur(24px)', boxShadow: '0 25px 60px rgba(0,0,0,0.12)' }}
          data-testid="resume-modal"
        >
          {/* Terminal Header */}
          <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
            <div className="flex items-center gap-3">
              <Fingerprint size={20} />
              <span className="text-mono text-xs" style={{ color: 'var(--text-secondary)', letterSpacing: '0.15em' }}>IDENTITÄTS-TERMINAL</span>
            </div>
            <button onClick={() => { onClose(); resetModal(); }} className="text-mono text-xs px-2 py-1 hover:bg-white hover:text-black transition-colors"
              style={{ border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}
              data-testid="resume-modal-close">
              ESC
            </button>
          </div>

          <div className="p-6">
            {/* Input Phase */}
            {phase === 'input' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-mono text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Geben Sie Ihre Subject-ID oder E-Mail-Adresse ein, um den Zugriff auf Ihr Dossier zu reaktivieren.
                </p>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="text-overline block mb-2">SUBJECT-ID ODER E-MAIL</label>
                    <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="#325CAC48 oder name@example.com"
                      className="input-brutal w-full" autoFocus data-testid="resume-identifier-input" />
                  </div>
                  <button type="submit" disabled={loading || !identifier.trim()}
                    className="btn-brutal btn-brutal-filled w-full flex items-center justify-center gap-3"
                    data-testid="resume-submit-btn">
                    {loading ? (
                      <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                        VERIFIZIERE...
                      </motion.span>
                    ) : (
                      <>
                        <Lock size={18} weight="bold" />
                        ZUGRIFF ANFORDERN
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Success Phase */}
            {phase === 'success' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                <div className="mb-6 py-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
                  <Check size={40} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
                  <p className="text-mono text-sm" style={{ color: '#22C55E' }}>ZUGANGSSCHLÜSSEL GENERIERT</p>
                  {subjectId && <p className="text-mono text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Subject #{subjectId}</p>}
                </div>
                <p className="text-mono text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Prüfen Sie Ihr Terminal (Konsole/E-Mail).
                </p>
                {debugToken && (
                  <button onClick={handleDirectAccess} disabled={loading}
                    className="btn-brutal btn-brutal-filled w-full flex items-center justify-center gap-3"
                    data-testid="resume-direct-access-btn">
                    {loading ? 'VERIFIZIERE...' : (
                      <>DIREKT ZUM DASHBOARD <ArrowRight size={18} weight="bold" /></>
                    )}
                  </button>
                )}
              </motion.div>
            )}

            {/* Error Phase */}
            {phase === 'error' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                <div className="mb-6 py-4">
                  <Warning size={40} className="mx-auto mb-3" style={{ color: 'var(--accent-cyan)' }} />
                  <p className="text-mono text-sm" style={{ color: 'var(--accent-cyan)' }}>ZUGRIFF VERWEIGERT</p>
                </div>
                <p className="text-mono text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{errorMsg}</p>
                <button onClick={() => { setPhase('input'); setErrorMsg(''); }}
                  className="btn-brutal w-full" data-testid="resume-retry-btn">
                  ERNEUT VERSUCHEN
                </button>
              </motion.div>
            )}
          </div>

          {/* Terminal Footer */}
          <div className="px-6 py-3 flex justify-between items-center" style={{ borderTop: '1px solid var(--border-default)' }}>
            <span className="text-mono text-xs" style={{ color: 'var(--text-secondary)' }}>VERSCHLÜSSELT · 24H GÜLTIGKEIT</span>
            <span className="text-mono text-xs" style={{ color: 'var(--border-default)' }}>v2.0</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const DEEPDIVE_QUOTES = [
  { text: "Der einzige Weg, grossartige Arbeit zu leisten, ist zu lieben, was man tut.", author: "Steve Jobs" },
  { text: "Erkenne dich selbst.", author: "Sokrates" },
  { text: "Was hinter uns liegt und was vor uns liegt, sind winzige Angelegenheiten im Vergleich zu dem, was in uns liegt.", author: "Ralph Waldo Emerson" },
  { text: "Der Mensch ist nichts anderes als das, wozu er sich macht.", author: "Jean-Paul Sartre" },
  { text: "Man entdeckt keine neuen Erdteile, ohne den Mut zu haben, alte Kuesten aus den Augen zu verlieren.", author: "Andre Gide" }
];

const getStatusLabel = (val) => {
  if (val <= 12) return 'Voellig orientierungslos';
  if (val <= 37) return 'Suchend';
  if (val <= 62) return 'Teilweise klar';
  if (val <= 87) return 'Fokussiert';
  return 'Maximale Klarheit';
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [creating, setCreating] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return localStorage.getItem('deepdive_entry_seen') === '1'; }
    catch { return false; }
  });
  const [quoteIdx, setQuoteIdx] = useState(0);

  const enterOnboarding = () => {
    try { localStorage.setItem('deepdive_entry_seen', '1'); } catch {}
    setShowOnboarding(true);
  };
  const [name, setName] = useState('');
  const [alter, setAlter] = useState(30);
  const [statusLevel, setStatusLevel] = useState(50);

  useEffect(() => {
    if (location.state?.showResume) { setShowResumeModal(true); window.history.replaceState({}, document.title); }
  }, [location.state]);

  useEffect(() => {
    const interval = setInterval(() => setQuoteIdx(i => (i + 1) % DEEPDIVE_QUOTES.length), 6000);
    return () => clearInterval(interval);
  }, []);

  const getAgeGroup = (age) => {
    if (age < 25) return 'entdecker';
    if (age < 35) return 'performer';
    if (age < 45) return 'strategist';
    if (age < 55) return 'mentor';
    return 'visionaer';
  };

  // 12 DEEPDIVE Chronos stages
  const LIFE_STAGES = [
    { min: 16, max: 19, name: 'Initiator', focus: 'Grundkonfiguration & Potenzial-Erkennung' },
    { min: 20, max: 24, name: 'Explorer', focus: 'Kalibrierung & erste operative Schritte' },
    { min: 25, max: 29, name: 'Ascender', focus: 'Aufbau der Hardware (Karriere/Status)' },
    { min: 30, max: 35, name: 'Performer', focus: 'Optimierung der Effizienz & Output' },
    { min: 36, max: 40, name: 'Strategist', focus: 'Systemische Konsolidierung & Führung' },
    { min: 41, max: 47, name: 'Architect', focus: 'Neuausrichtung & strukturelle Tiefe' },
    { min: 48, max: 53, name: 'Mentor', focus: 'Wissenstransfer & Souveränität' },
    { min: 54, max: 57, name: 'Governor', focus: 'Kontrolle & langfristige Sicherung' },
    { min: 58, max: 62, name: 'Sage', focus: 'Reflexion & strategische Ruhe' },
    { min: 63, max: 67, name: 'Legacy-Builder', focus: 'Vorbereitung der finalen Projektion' },
    { min: 68, max: 73, name: 'Visionary', focus: 'Der Blick zurück und weit voraus' },
    { min: 74, max: 80, name: 'Eternalist', focus: 'Überdauernde Sinnstiftung & Essenz' },
  ];
  const getLifeStage = (age) => LIFE_STAGES.find(s => age >= s.min && age <= s.max) || LIFE_STAGES[3];

  const [startError, setStartError] = useState('');
  const handleStart = async () => {
    setCreating(true);
    setStartError('');
    try {
      const response = await axios.post(`${API}/sessions`, { age_group: getAgeGroup(alter) });
      const sid = response.data.session_id;
      if (name.trim()) {
        sessionStorage.setItem(`deepdive_onboard_${sid}`, JSON.stringify({ name: name.trim(), alter, statusLevel }));
      }
      navigate(`/dashboard/${sid}`);
    } catch (error) {
      console.error('DEEPDIVE Start Error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Start fehlgeschlagen. Bitte erneut versuchen.';
      setStartError(typeof msg === 'string' ? msg : 'Start fehlgeschlagen.');
      setCreating(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!showOnboarding ? (
        <motion.div key="entry"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: -40, transition: { duration: 0.5 } }}
          className="page-shell-bg" style={{ alignItems: 'center', paddingTop: '0', minHeight: '100vh' }}
          data-testid="entry-page">
          {/* Top right controls */}
          <div className="fixed top-4 right-4 flex items-center gap-2 z-20">
            <ThemeToggle />
            <button onClick={() => setShowResumeModal(true)} className="page-shell-btn page-shell-btn-outline"
              style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.7rem' }} data-testid="entry-resume-btn">
              <Lock size={11} weight="bold" /> Fortsetzen
            </button>
          </div>

          <div className="w-full max-w-3xl px-6 py-12 md:py-20">
            {/* Pulsing Fingerprint */}
            <div className="flex justify-center mb-10">
              <motion.div
                animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="hex-clip flex items-center justify-center"
                style={{ width: 96, height: 96, background: 'var(--accent-cyan)', boxShadow: '0 0 40px rgba(91,184,196,0.35)' }}>
                <Fingerprint size={48} weight="duotone" color="#fff" />
              </motion.div>
            </div>

            {/* Brand */}
            <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-center text-xs font-bold mb-3" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.4em' }}>
              D E E P D I V E
            </motion.p>

            {/* Headline */}
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="text-center font-black mb-3 leading-tight"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', letterSpacing: '-0.025em', color: 'var(--text-primary)' }}>
              Das Assessment deiner<br /> wahren Identität.
            </motion.h1>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="mb-10" />

            {/* Rotating Quote */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="text-center mb-14 min-h-[120px] flex flex-col justify-center px-4">
              <AnimatePresence mode="wait">
                <motion.div key={quoteIdx} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.6 }}>
                  <p className="italic leading-relaxed mb-3" style={{ fontSize: 'clamp(1.05rem, 2vw, 1.4rem)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                    "{DEEPDIVE_QUOTES[quoteIdx].text}"
                  </p>
                  <p className="text-xs font-semibold tracking-wider" style={{ color: 'var(--text-secondary)', letterSpacing: '0.15em' }}>
                    — {DEEPDIVE_QUOTES[quoteIdx].author.toUpperCase()}
                  </p>
                </motion.div>
              </AnimatePresence>
              <div className="flex justify-center gap-2 mt-5">
                {DEEPDIVE_QUOTES.map((_, i) => (
                  <button key={i} onClick={() => setQuoteIdx(i)} aria-label={`Zitat ${i + 1}`}
                    className="w-1.5 h-1.5 rounded-full cursor-pointer transition-all"
                    style={{
                      background: i === quoteIdx ? 'var(--accent-cyan)' : 'var(--border-default)',
                      transform: i === quoteIdx ? 'scale(1.6)' : 'scale(1)',
                    }} />
                ))}
              </div>
            </motion.div>

            {/* 3 Fragen-Kästen (Inhalte aus der ehemaligen Sprechblase) */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3"
              data-testid="entry-question-boxes">
              {[
                { q: 'Wer bin ich?', sub: 'Die Wahrheit hinter der Fassade' },
                { q: 'Wozu bin ich geeignet?', sub: 'Habe ich das Zeug dazu?' },
                { q: 'Wer will ich sein?', sub: 'Der Bauplan deines zukünftigen Ich' },
              ].map((item, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65 + i * 0.08, type: 'spring', damping: 22, stiffness: 220 }}
                  className="p-5 text-center"
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '18px',
                  }}>
                  <p className="font-bold mb-2" style={{
                    fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                  }}>
                    {item.q}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {item.sub}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* 3 Säulen */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-12">
              {[
                { icon: Eye, title: 'Authentizität', text: 'Wir finden die Lücke zwischen deinem Schein und deinem Sein.' },
                { icon: Brain, title: 'Hardware', text: 'Analyse deiner kognitiven und physischen Architektur.' },
                { icon: Compass, title: 'Projektion', text: 'Der strategische Bauplan für dein zukünftiges Ich.' }
              ].map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="p-5 text-center" style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '18px',
                  }}>
                    <Icon size={26} weight="thin" className="mx-auto mb-3" style={{ color: 'var(--accent-cyan)' }} />
                    <p className="text-xs font-bold mb-2" style={{ color: 'var(--text-primary)', letterSpacing: '0.08em' }}>{p.title.toUpperCase()}</p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>{p.text}</p>
                  </div>
                );
              })}
            </motion.div>

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
              className="flex justify-center mb-10">
              <button onClick={enterOnboarding} data-testid="entry-cta-btn"
                className="px-8 py-4 flex items-center gap-3 transition-all hover:scale-[1.03]"
                style={{
                  background: 'var(--accent-cyan)', color: '#fff', border: 'none',
                  borderRadius: '14px', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.05em',
                  boxShadow: '0 8px 28px rgba(91,184,196,0.4)', cursor: 'pointer'
                }}>
                DEEPDIVE INITIALISIEREN
                <ArrowRight size={18} weight="bold" />
              </button>
            </motion.div>

          </div>

          {/* Resume Modal */}
          <ResumeModal isOpen={showResumeModal} onClose={() => setShowResumeModal(false)} />
        </motion.div>
      ) : (
        <motion.div key="onboarding"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="page-shell-bg" style={{ alignItems: 'flex-start', paddingTop: '1.5rem' }}>
          <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-xl page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Fingerprint size={20} weight="bold" color="#fff" />
            </div>
            <span className="text-xl font-bold tracking-tight">DEEPDIVE</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={() => setShowResumeModal(true)} className="page-shell-btn page-shell-btn-outline"
              style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.75rem' }} data-testid="landing-resume-btn">
              <Lock size={12} weight="bold" /> Fortsetzen
            </button>
          </div>
        </div>
        <div className="page-shell-body">
          {/* Quotes */}
          <div className="text-center mb-8 min-h-[110px] flex flex-col justify-center px-2">
            <AnimatePresence mode="wait">
              <motion.div key={quoteIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5 }}>
                <p className="italic leading-relaxed mb-3"
                  style={{ fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  "{DEEPDIVE_QUOTES[quoteIdx].text}"
                </p>
                <p className="text-xs font-semibold tracking-wider"
                  style={{ color: 'var(--text-secondary)', letterSpacing: '0.15em' }}>
                  — {DEEPDIVE_QUOTES[quoteIdx].author.toUpperCase()}
                </p>
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-center gap-1.5 mt-4">
              {DEEPDIVE_QUOTES.map((_, i) => (
                <div key={i} onClick={() => setQuoteIdx(i)} className="w-1.5 h-1.5 rounded-full cursor-pointer transition-all"
                  style={{
                    background: i === quoteIdx ? 'var(--accent-cyan)' : 'var(--border-default)',
                    transform: i === quoteIdx ? 'scale(1.5)' : 'scale(1)',
                  }} />
              ))}
            </div>
          </div>
          {/* Separator */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>Dein Profil</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
          </div>
          {/* Name */}
          <div className="mb-5">
            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Wie heisst du?</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname"
              className="page-shell-input" data-testid="landing-name" autoFocus />
          </div>
          {/* Age Slider */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Alter</label>
              <span className="text-lg font-bold" style={{ color: 'var(--accent-cyan)' }}>{alter}</span>
            </div>
            <input type="range" min="16" max="80" value={alter} onChange={(e) => setAlter(parseInt(e.target.value))}
              className="w-full h-2 appearance-none cursor-pointer rounded-full"
              style={{ background: `linear-gradient(to right, var(--accent-cyan) 0%, var(--accent-cyan) ${((alter - 16) / 64) * 100}%, var(--border-default) ${((alter - 16) / 64) * 100}%, var(--border-default) 100%)`, borderRadius: '9999px', outline: 'none', WebkitAppearance: 'none' }}
              data-testid="landing-age-slider" />
            <div className="flex justify-between mt-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>16</span>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>80</span>
            </div>
            {/* Chronos Life-Stage Reveal */}
            <div className="mt-2 p-2.5 flex items-center justify-between gap-3"
              style={{ background: 'rgba(91,184,196,0.08)', border: '1px solid rgba(91,184,196,0.25)', borderRadius: '10px' }}
              data-testid="landing-life-stage">
              <div>
                <p className="text-[10px] font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>DEEPDIVE-STATUS</p>
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{getLifeStage(alter).name}</p>
              </div>
              <p className="text-[10px] text-right flex-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                {getLifeStage(alter).focus}
              </p>
            </div>
          </div>
          {/* Status Slider */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Aktuelle Situation</label>
              <span className="text-sm font-bold" style={{ color: 'var(--accent-gold)' }}>{getStatusLabel(statusLevel)}</span>
            </div>
            <input type="range" min="0" max="100" value={statusLevel} onChange={(e) => setStatusLevel(parseInt(e.target.value))}
              className="w-full h-2 appearance-none cursor-pointer rounded-full"
              style={{ background: `linear-gradient(to right, var(--accent-gold) 0%, var(--accent-gold) ${statusLevel}%, var(--border-default) ${statusLevel}%, var(--border-default) 100%)`, borderRadius: '9999px', outline: 'none', WebkitAppearance: 'none' }}
              data-testid="landing-status-slider" />
            <div className="flex justify-between mt-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Orientierungslos</span>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Maximale Klarheit</span>
            </div>
          </div>
          {/* CTA */}
          <form onSubmit={(e) => { e.preventDefault(); handleStart(); }}>
            <button type="submit" disabled={creating} className="page-shell-btn w-full text-base"
              style={{ padding: '1rem', position: 'relative', zIndex: 10 }} data-testid="start-assessment-btn">
              {creating ? 'Starte...' : 'DEEPDIVE starten'} <ArrowRight size={20} weight="bold" />
            </button>
          </form>
          {startError && (
            <p className="text-center text-xs mt-3" style={{ color: '#DC2626' }} data-testid="landing-start-error">
              {startError}
            </p>
          )}
          <footer className="mt-8 pt-4 text-center" style={{ borderTop: '1px solid var(--border-default)' }}>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>2026 DEEPDIVE</p>
            <button onClick={() => setShowResumeModal(true)} className="text-xs mt-1"
              style={{ color: 'var(--accent-cyan)', background: 'transparent', border: 'none', cursor: 'pointer' }} data-testid="resume-link">
              Bestehenden Deepdive fortsetzen
            </button>
          </footer>
          <ResumeModal isOpen={showResumeModal} onClose={() => setShowResumeModal(false)} />
        </div>
      </motion.div>
    </motion.div>
      )}
    </AnimatePresence>
  );
};

const QuickScanPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/questions/quick-scan/${sessionId}`);
        setQuestions(response.data.questions);
      } catch (error) {
        console.error('Error fetching questions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [sessionId]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Submit quick scan
      setSubmitting(true);
      try {
        await axios.post(`${API}/assessment/quick-scan`, {
          session_id: sessionId,
          answers
        });
        navigate(`/dashboard/${sessionId}`);
      } catch (error) {
        console.error('Error submitting quick scan:', error);
        setSubmitting(false);
      }
    }
  };

  if (loading) return <LoadingScreen text="LADE QUICK-SCAN..." />;

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion?.id];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="page-shell-bg">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card">
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Fingerprint size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Quick-Scan</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Frage {currentIndex + 1} von {questions.length}</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="h-1" style={{ background: 'var(--border-default)' }}>
          <div className="h-full rounded-r" style={{ width: `${progress}%`, background: 'var(--accent-cyan)', transition: 'width 0.4s' }} />
        </div>

        <div className="page-shell-body">
          <AnimatePresence mode="wait">
            <motion.div key={currentQuestion?.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="mb-8">
              <h2 className="text-xl font-bold mb-6 leading-tight">{currentQuestion?.text}</h2>

              {currentQuestion?.type === 'choice' && (
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <div key={option.value} onClick={() => handleAnswer(currentQuestion.id, option.value)}
                      className={`page-shell-tag flex items-center gap-3 text-left ${currentAnswer === option.value ? 'page-shell-tag-selected' : ''}`}
                      data-testid={`option-${option.value}`}>
                      <div className="w-5 h-5 rounded-lg border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: currentAnswer === option.value ? '#fff' : 'var(--border-default)', background: currentAnswer === option.value ? '#fff' : 'transparent' }}>
                        {currentAnswer === option.value && <Check size={14} weight="bold" style={{ color: 'var(--accent-cyan)' }} />}
                      </div>
                      <span className="text-sm">{option.text}</span>
                    </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}
              className="page-shell-btn page-shell-btn-outline" style={{ width: 'auto', padding: '0.625rem 1.25rem' }}
              data-testid="prev-btn">
              Zurueck
            </button>
            <button onClick={handleNext} disabled={!currentAnswer || submitting}
              className="page-shell-btn" style={{ width: 'auto', padding: '0.625rem 1.25rem' }}
              data-testid="next-btn">
              {currentIndex === questions.length - 1 ? (submitting ? 'Wird gespeichert...' : 'Abschliessen') : 'Weiter'}
              <CaretRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const CheckoutPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [betaCode, setBetaCode] = useState('');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaVerified, setBetaVerified] = useState(false);
  const [betaError, setBetaError] = useState('');
  const cancelled = searchParams.get('cancelled');

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/create-checkout-session`, {
        session_id: sessionId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      setLoading(false);
    }
  };

  const handleTestBypass = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/sessions/${sessionId}/unlock-deep-scan`);
      navigate(`/deep-scan/${sessionId}`);
    } catch (error) {
      console.error('Error bypassing payment:', error);
      setTestLoading(false);
    }
  };

  const handleApplyBeta = async () => {
    if (!betaCode.trim()) return;
    setBetaLoading(true);
    setBetaError('');
    try {
      await axios.post(`${API}/verify-bypass`, { session_id: sessionId, code: betaCode.trim() });
      setBetaVerified(true);
    } catch (error) {
      setBetaError(error?.response?.data?.detail || 'Ungültiger Beta-Code.');
    } finally {
      setBetaLoading(false);
    }
  };

  return (
    <div className="page-shell-bg">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-lg page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Lock size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Zugang freischalten</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Quick-Scan abgeschlossen</p>
            </div>
          </div>
        </div>
        <div className="page-shell-body">
          {cancelled && (
            <div className="mb-6 p-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--accent-cyan)', borderRadius: '12px' }}>
              <div className="flex items-center gap-3">
                <Warning size={20} style={{ color: 'var(--accent-cyan)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--accent-cyan)' }}>Zahlung abgebrochen</span>
              </div>
            </div>
          )}

          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
            Du hast den Quick-Scan abgeschlossen. Der <strong style={{ color: 'var(--text-primary)' }}>Deep-Scan</strong> enthaelt:
          </p>

          <div className="space-y-2.5 mb-6">
            {['55 tiefgreifende Fragen', 'Shadow-Task unter Zeitdruck', 'KI-generiertes Dossier', 'Detaillierte Skill-Analyse', 'Personalisierte Handlungsempfehlungen'].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <Check size={16} style={{ color: 'var(--accent-green)' }} /> {item}
              </div>
            ))}
          </div>

          <div className="text-center mb-6">
            <span className="text-4xl font-bold">19,99</span>
            <span className="text-sm ml-1" style={{ color: 'var(--text-secondary)' }}>EUR · Einmalig</span>
          </div>

          {betaVerified ? (
            <>
              <div className="mb-3 p-3 flex items-center gap-3" data-testid="beta-success-banner"
                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid var(--accent-green)', borderRadius: '12px' }}>
                <Check size={18} style={{ color: 'var(--accent-green)' }} weight="bold" />
                <span className="text-sm font-medium" style={{ color: 'var(--accent-green)' }}>Beta-Zugang verifiziert</span>
              </div>
              <button onClick={() => navigate(`/strategic-projection/${sessionId}`)}
                className="page-shell-btn mb-3" data-testid="beta-continue-btn">
                Projektion jetzt freischalten <ArrowRight size={18} weight="bold" />
              </button>
            </>
          ) : (
            <>
              <button onClick={handleCheckout} disabled={loading}
                className="page-shell-btn mb-3" data-testid="checkout-btn">
                {loading ? 'Wird verarbeitet...' : 'Deep-Scan freischalten'} <ArrowRight size={18} weight="bold" />
              </button>

              {/* Beta-Code (optional) */}
              <div className="mb-3 mt-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={betaCode}
                    onChange={(e) => { setBetaCode(e.target.value); setBetaError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApplyBeta(); }}
                    placeholder="Beta-Code (optional)"
                    data-testid="beta-code-input"
                    style={{
                      flex: 1,
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                      borderRadius: '12px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      letterSpacing: '0.04em',
                      outline: 'none',
                    }}
                  />
                  <button onClick={handleApplyBeta} disabled={betaLoading || !betaCode.trim()}
                    data-testid="apply-beta-btn"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--accent-cyan)',
                      color: 'var(--accent-cyan)',
                      borderRadius: '12px',
                      padding: '10px 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: betaLoading || !betaCode.trim() ? 'default' : 'pointer',
                      opacity: betaLoading || !betaCode.trim() ? 0.5 : 1,
                      whiteSpace: 'nowrap',
                    }}>
                    {betaLoading ? '...' : 'Code anwenden'}
                  </button>
                </div>
                {betaError && (
                  <p className="text-xs mt-2" data-testid="beta-error" style={{ color: '#EF4444' }}>{betaError}</p>
                )}
              </div>

              {!IS_PRODUCTION && (
                <button onClick={handleTestBypass} disabled={testLoading}
                  className="page-shell-btn page-shell-btn-outline" data-testid="test-bypass-btn"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                  {testLoading ? 'Wird freigeschaltet...' : 'Test-Modus (ohne Zahlung)'}
                </button>
              )}
            </>
          )}

          <p className="text-xs mt-4 text-center" style={{ color: 'var(--text-secondary)' }}>
            Sichere Zahlung ueber Stripe. Keine Abonnements.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const checkoutSessionId = searchParams.get('checkout_session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sealFeld1 = useCallback(async () => {
    // Load form data from sessionStorage and seal Feld 1 (PII only kept for the active tab session)
    const LS_KEY = 'archive_feld1_draft';
    const saved = sessionStorage.getItem(`${LS_KEY}_${sessionId}`);
    if (!saved) return;
    try {
      const form = JSON.parse(saved);
      await axios.post(`${API}/user/feld1`, {
        session_id: sessionId,
        name: form.name?.trim(),
        geburtsdatum: form.geburtsdatum,
        groesse_cm: parseInt(form.groesse_cm),
        gewicht_kg: parseFloat(form.gewicht_kg),
        aktueller_job: form.aktueller_job?.trim(),
        erwartung: form.erwartung,
        geschlecht: form.geschlecht || null,
        lebensstatus: form.lebensstatus || null,
        leidenschaft_hobby: form.leidenschaft_hobby?.trim() || null,
        intensitaet: form.leidenschaft_hobby?.trim() ? form.intensitaet : null,
        energie_kategorien: form.energie_kategorien?.length > 0 ? form.energie_kategorien : null
      });
      sessionStorage.removeItem(`${LS_KEY}_${sessionId}`);
    } catch (err) {
      console.error('Seal error after payment:', err);
    }
  }, [sessionId]);

  const checkPaymentStatus = useCallback(async () => {
    if (!checkoutSessionId) return;
    
    try {
      const response = await axios.get(`${API}/payment-status/${checkoutSessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('sealing');
        await sealFeld1();
        setStatus('success');
        setTimeout(() => navigate(`/dashboard/${sessionId}`), 2000);
      } else if (attempts < 5) {
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(), 2000);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < 5) {
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [checkoutSessionId, sessionId, navigate, attempts, sealFeld1]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  return (
    <div className="page-shell-bg">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-md page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: status === 'success' ? 'var(--accent-green)' : 'var(--accent-cyan)' }}>
              {status === 'success' ? <Check size={20} weight="bold" color="#fff" /> : <Fingerprint size={20} weight="bold" color="#fff" />}
            </div>
            <h2 className="text-lg font-bold">Zahlung</h2>
          </div>
        </div>
        <div className="page-shell-body text-center py-8">
          {(status === 'checking' || status === 'sealing') && (
            <>
              <div className="loading-bar w-48 mx-auto mb-6"><div className="loading-bar-fill" /></div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {status === 'sealing' ? 'Identitaet wird versiegelt...' : 'Zahlung wird verifiziert...'}
              </p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="hex-clip mx-auto flex items-center justify-center mb-4" style={{ width: 64, height: 64, background: 'var(--accent-green)' }}>
                <Check size={32} weight="bold" color="#fff" />
              </div>
              <h3 className="text-xl font-bold mb-2">Identitaet versiegelt</h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Du wirst zum Dashboard weitergeleitet...</p>
            </>
          )}
          {status === 'pending' && (
            <>
              <Clock size={48} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
              <h3 className="text-xl font-bold mb-2">Zahlung ausstehend</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Die Zahlung wird noch verarbeitet.</p>
              <button onClick={() => navigate(`/dashboard/${sessionId}`)} className="page-shell-btn">Zum Dashboard</button>
            </>
          )}
          {status === 'error' && (
            <>
              <Warning size={48} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
              <h3 className="text-xl font-bold mb-2">Fehler</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Zahlungsverifizierung fehlgeschlagen.</p>
              <button onClick={() => navigate(`/dashboard/${sessionId}`)} className="page-shell-btn">Zum Dashboard</button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const DeepScanPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [shadowTimer, setShadowTimer] = useState(15);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showShadowTask, setShowShadowTask] = useState(false);
  const [shadowTaskData, setShadowTaskData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        // Load ALL questions (quick + deep) for the full Geist scan
        const [quickRes, deepRes] = await Promise.all([
          axios.get(`${API}/questions/quick-scan/${sessionId}`),
          axios.get(`${API}/questions/deep-scan/${sessionId}`)
        ]);
        const allQuestions = [
          ...(quickRes.data.questions || []),
          ...(deepRes.data.questions || [])
        ];
        setQuestions(allQuestions);
        setShadowTimer(deepRes.data.shadow_task_timer || 15);
      } catch (error) {
        console.error('Error fetching questions:', error);
        if (error.response?.status === 403) {
          setError('Bitte zuerst Feld 1 versiegeln');
          setTimeout(() => navigate(`/dashboard/${sessionId}`), 2000);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [sessionId, navigate]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      // Show shadow task at specific point (after block 5, before shadow_self block)
      if (currentIndex === 49) { // After 50 questions
        setShowShadowTask(true);
      }
    } else if (!shadowTaskData) {
      setShowShadowTask(true);
    } else {
      handleSubmit();
    }
  };

  const handleShadowTaskComplete = (data) => {
    setShadowTaskData(data);
    setShowShadowTask(false);
    if (currentIndex === questions.length - 1) {
      handleSubmit(data);
    }
  };

  const handleSubmit = async (shadowData = shadowTaskData) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment/deep-scan`, {
        session_id: sessionId,
        answers,
        shadow_task_data: shadowData
      });
      navigate(`/dashboard/${sessionId}`);
    } catch (error) {
      console.error('Error submitting deep scan:', error);
      setSubmitting(false);
    }
  };

  // Show saving screen
  if (submitting) {
    return (
      <div className="page-shell-bg">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md page-shell-card">
          <div className="page-shell-body text-center py-12">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} className="mx-auto mb-6">
              <Brain size={48} style={{ color: 'var(--accent-cyan)' }} />
            </motion.div>
            <h3 className="text-xl font-bold mb-3">Geist-Scan abgeschlossen</h3>
            <div className="loading-bar w-48 mx-auto mb-4"><div className="loading-bar-fill" /></div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Antworten werden archiviert...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) return <LoadingScreen text="Lade Geist-Scan..." />;
  if (error) return <LoadingScreen text={error} />;
  if (showShadowTask) return <ShadowTaskScreen timer={shadowTimer} onComplete={handleShadowTaskComplete} />;

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion?.id];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="page-shell-bg">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card">
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Brain size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Block 1: Geist</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {currentQuestion?.block ? currentQuestion.block.replace(/_/g, ' ') : 'Mentale Analyse'}
              </p>
            </div>
          </div>
          <span className="text-xs px-3 py-1 font-semibold" style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: 'var(--text-secondary)' }}>
            {currentIndex + 1} / {questions.length}
          </span>
        </div>

        {/* Progress */}
        <div className="h-1" style={{ background: 'var(--border-default)' }}>
          <div className="h-full" style={{ width: `${progress}%`, background: 'var(--accent-cyan)', borderRadius: '0 4px 4px 0', transition: 'width 0.4s' }} />
        </div>

        <div className="page-shell-body">
          <AnimatePresence mode="wait">
            <motion.div key={currentQuestion?.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="mb-8">
              <h2 className="text-xl font-bold mb-6 leading-tight">{currentQuestion?.text}</h2>

              {currentQuestion?.type === 'choice' && (
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <div key={option.value} onClick={() => handleAnswer(currentQuestion.id, option.value)}
                      className={`page-shell-tag flex items-center gap-3 text-left ${currentAnswer === option.value ? 'page-shell-tag-selected' : ''}`}>
                      <div className="w-5 h-5 rounded-lg border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: currentAnswer === option.value ? '#fff' : 'var(--border-default)', background: currentAnswer === option.value ? '#fff' : 'transparent' }}>
                        {currentAnswer === option.value && <Check size={14} weight="bold" style={{ color: 'var(--accent-cyan)' }} />}
                      </div>
                      <span className="text-sm">{option.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {currentQuestion?.type === 'scale' && (
                <div className="flex gap-2">
                  {Array.from({ length: currentQuestion.scale_max }, (_, i) => i + 1).map((value) => (
                    <div key={value} onClick={() => handleAnswer(currentQuestion.id, value)}
                      className={`page-shell-tag flex-1 text-center ${currentAnswer === value ? 'page-shell-tag-selected' : ''}`}>
                      <div className="text-2xl font-bold mb-1">{value}</div>
                      <div className="text-xs" style={{ color: currentAnswer === value ? 'inherit' : 'var(--text-secondary)' }}>
                        {currentQuestion.scale_labels?.[value - 1]}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {currentQuestion?.type === 'text' && (
                <VoiceTextarea value={currentAnswer || ''} onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                  placeholder={currentQuestion.placeholder || 'Deine Antwort...'} rows={4}
                  testId={`geist-question-${currentQuestion.id}`} />
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between items-center">
            <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}
              className="page-shell-btn page-shell-btn-outline" style={{ width: 'auto', padding: '0.625rem 1.25rem' }}>
              Zurueck
            </button>
            <button onClick={handleNext} disabled={!currentAnswer || submitting}
              className="page-shell-btn" style={{ width: 'auto', padding: '0.625rem 1.25rem' }}>
              {submitting ? 'Wird gespeichert...' : currentIndex === questions.length - 1 ? 'Abschliessen' : 'Weiter'}
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const ShadowTaskScreen = ({ timer, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState(timer);
  const [dotPosition, setDotPosition] = useState({ x: 50, y: 50 });
  const [clicked, setClicked] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  useEffect(() => {
    if (showInstructions) return;
    
    setStartTime(Date.now());
    
    // Move dot randomly
    const moveInterval = setInterval(() => {
      setDotPosition({
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 20
      });
    }, 800);

    // Countdown
    const timerInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          clearInterval(moveInterval);
          if (!clicked) {
            onComplete({ clicked: false, reaction_time_ms: null });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(moveInterval);
      clearInterval(timerInterval);
    };
  }, [showInstructions, timer, clicked, onComplete]);

  const handleDotClick = () => {
    if (clicked) return;
    setClicked(true);
    const reactionTime = Date.now() - startTime;
    onComplete({ clicked: true, reaction_time_ms: reactionTime });
  };

  if (showInstructions) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center max-w-lg px-4"
        >
          <Warning size={64} className="mx-auto mb-6" style={{ color: 'var(--accent-cyan)' }} />
          <h1 className="heading-large mb-4">SHADOW-TASK</h1>
          <p className="text-mono mb-8" style={{ color: 'var(--text-secondary)' }}>
            Ein <span style={{ color: 'var(--accent-blue)' }}>blauer Punkt</span> wird auf dem Bildschirm erscheinen und sich bewegen. 
            Klicke so schnell wie möglich darauf. Du hast <span style={{ color: 'var(--text-primary)' }}>{timer} Sekunden</span>.
          </p>
          <button
            onClick={() => setShowInstructions(false)}
            className="btn-brutal btn-brutal-red"
            data-testid="start-shadow-task"
          >
            BEREIT
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--bg-void)' }}>
      <NoiseOverlay />
      
      {/* Timer */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center">
        <div className={`timer-brutal ${timeLeft <= 5 ? 'timer-warning' : ''}`}>
          {timeLeft}
        </div>
        <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>SEKUNDEN</p>
      </div>

      {/* The Dot */}
      {!clicked && timeLeft > 0 && (
        <motion.div
          className="shadow-dot absolute"
          style={{ 
            left: `${dotPosition.x}%`, 
            top: `${dotPosition.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
          animate={{ 
            left: `${dotPosition.x}%`, 
            top: `${dotPosition.y}%` 
          }}
          transition={{ duration: 0.3 }}
          onClick={handleDotClick}
          data-testid="shadow-dot"
        />
      )}

      {/* Success message */}
      {clicked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-center"
          >
            <Check size={64} className="mx-auto mb-4" style={{ color: '#22C55E' }} />
            <p className="heading-large">ERFASST</p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const DossierPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [ageGroup, setAgeGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const response = await axios.get(`${API}/dossier/${sessionId}`);
        setDossier(response.data.dossier);
        setAgeGroup(response.data.age_group_info);
      } catch (error) {
        console.error('Error fetching dossier:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [sessionId]);

  if (loading) return <LoadingScreen text="LADE DOSSIER..." />;
  if (!dossier) return <LoadingScreen text="DOSSIER NICHT GEFUNDEN" />;

  // Parse the analysis text into sections
  const sections = dossier.analysis_text?.split(/\d+\.\s+[A-ZÄÖÜ-]+/).filter(Boolean) || [];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-void)' }}>
      <NoiseOverlay />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Fingerprint size={48} />
              <div>
                <span className="text-overline">{ageGroup?.name || 'DOSSIER'}</span>
                <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {ageGroup?.tagline}
                </p>
              </div>
            </div>
            <div className="stamp-classified">
              DEKLASSIFIZIERT
            </div>
          </div>
          
          <h1 className="heading-massive">
            PSYCHOLOGISCHES<br />DOSSIER
          </h1>
        </motion.header>

        {/* Skill Bars */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-overline mb-6">KOMPETENZ-PROFIL</h2>
          <div className="space-y-3">
            {dossier.skill_scores && Object.entries(dossier.skill_scores).map(([skill, score], index) => (
              <SkillBar 
                key={skill} 
                label={skill} 
                value={score} 
                delay={100 * (index + 1)}
              />
            ))}
          </div>
        </motion.section>

        {/* Analysis */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-overline mb-6">ANALYSE</h2>
          <div 
            className="card-brutal"
            style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
          >
            <div className="text-mono" style={{ color: 'var(--text-secondary)' }}>
              {dossier.analysis_text}
            </div>
          </div>
        </motion.section>

        {/* Footer */}
        <footer className="pt-8" style={{ borderTop: '1px solid var(--border-default)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <FileText size={24} />
              <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                Generiert am {new Date(dossier.generated_at).toLocaleDateString('de-DE')} · 
                Session: {sessionId.substring(0, 8)}...
              </p>
            </div>
            <button
              onClick={() => navigate(`/dashboard/${sessionId}`)}
              className="btn-brutal btn-brutal-filled flex items-center gap-3"
              data-testid="dossier-to-dashboard-btn"
            >
              WEITER ZUM DASHBOARD
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

// ==================== DASHBOARD ====================

const STAR_BLOCKS = [
  { id: 'hidden_talent', label: 'Hidden Talent' },
  { id: 'geist', label: 'Geist' },
  { id: 'koerper', label: 'Körper' },
  { id: 'wesen', label: 'Wesen' },
  { id: 'biografie', label: 'Biografie' },
  { id: 'kompass', label: 'Kompass' },
];

const ProgressStar = ({ filled, label, index }) => (
  <div className="relative group" style={{ width: 18, height: 18 }}>
    <AnimatePresence mode="wait">
      {filled ? (
        <motion.div
          key="filled"
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{
            scale: 1, rotate: 0, opacity: 1,
            filter: [
              'drop-shadow(0 0 0px rgba(245,158,11,0))',
              'drop-shadow(0 0 8px rgba(245,158,11,0.85))',
              'drop-shadow(0 0 4px rgba(245,158,11,0.6))',
            ],
          }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{
            scale: { type: 'spring', damping: 12, stiffness: 280, delay: index * 0.06 },
            rotate: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: index * 0.06 },
            filter: { duration: 1.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
          }}>
          <Star size={18} weight="fill" color="#F59E0B" />
        </motion.div>
      ) : (
        <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 0.35 }} exit={{ opacity: 0 }}>
          <Star size={18} weight="duotone" color="var(--text-secondary)" />
        </motion.div>
      )}
    </AnimatePresence>
    {/* Hover tooltip */}
    <div className="absolute opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
      style={{
        bottom: -24, left: '50%', transform: 'translateX(-50%)',
        background: 'var(--text-primary)', color: 'var(--bg-surface)',
        padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
        whiteSpace: 'nowrap', zIndex: 60,
      }}>
      {label}
    </div>
  </div>
);

const DashboardProgressBar = ({ percent, blockStatus = {} }) => {
  const filledCount = STAR_BLOCKS.filter(b => blockStatus[b.id] === 'done').length;
  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className="h-1" style={{ background: '#F1F5F9' }}>
        <motion.div
          className="h-full"
          style={{ background: 'var(--accent-cyan)', borderRadius: '0 4px 4px 0' }}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <div className="absolute right-4 top-2.5 hidden sm:flex flex-col items-end gap-1.5" data-testid="dashboard-progress-indicator">
        <div className="text-xs font-medium" style={{ color: 'var(--accent-cyan)' }}>
          {percent}% abgeschlossen
        </div>
        <div className="flex items-center gap-1.5"
          aria-label={`${filledCount} von ${STAR_BLOCKS.length} Blöcken abgeschlossen`}
          data-testid="dashboard-stars">
          {STAR_BLOCKS.map((b, i) => (
            <ProgressStar key={b.id} filled={blockStatus[b.id] === 'done'} label={b.label} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

const BlockCard = ({ block, status, onClick }) => {
  const Icon = block.icon;
  const isDone = status === 'done';

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(91,184,196,0.18)' }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative p-6 cursor-pointer h-full"
      style={{
        background: 'var(--bg-surface)',
        border: isDone ? '2px solid var(--text-primary)' : '2.5px solid #B91C1C',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--card-shadow)',
        minHeight: '200px'
      }}
      data-testid={`block-card-${block.id}`}
    >
      <div className="absolute top-4 right-4">
        {isDone ? (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
            className="flex items-center gap-1 px-3 py-1 text-xs font-semibold"
            style={{ background: 'var(--text-primary)', color: 'var(--bg-void)', borderRadius: '9999px' }}>
            <Check size={14} weight="bold" /> Fertig
          </motion.div>
        ) : (
          <span className="px-3 py-1 text-xs font-semibold"
            style={{ background: 'rgba(185,28,28,0.1)', color: '#B91C1C', borderRadius: '9999px' }}>
            Offen
          </span>
        )}
      </div>
      <div className="mb-4">
        <div className="hex-clip inline-flex items-center justify-center" style={{ width: 48, height: 48, background: isDone ? 'var(--text-primary)' : 'rgba(185,28,28,0.1)' }}>
          <Icon size={24} weight={isDone ? 'fill' : 'duotone'} style={{ color: isDone ? '#fff' : '#B91C1C' }} />
        </div>
      </div>
      <h3 className="text-xl font-bold mb-1">{block.name}</h3>
      <p className="text-xs font-semibold mb-3" style={{ color: isDone ? 'var(--text-primary)' : '#B91C1C', letterSpacing: '0.08em' }}>{block.subtitle}</p>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{block.description}</p>
      {!isDone && (
        <div className="absolute bottom-4 right-4 flex items-center gap-2 text-sm font-medium" style={{ color: '#B91C1C' }}>
          Starten <ArrowRight size={16} />
        </div>
      )}
    </motion.div>
  );
};

const ProjectionCard = ({ canAccess, onClick, blockStatus = {}, deepeningAnsweredCount = 0 }) => {
  const filled = STAR_BLOCKS.filter(b => blockStatus[b.id] === 'done').length;
  const total = STAR_BLOCKS.length;
  const remaining = total - filled;
  const hintText = remaining === 0
    ? 'Bereit · alle Blöcke offenbart'
    : remaining === 1
      ? 'Fast da — nur noch ein Block.'
      : remaining === total
        ? `Bereit, wenn du es bist — ${total} Blöcke warten.`
        : `Noch ${remaining} von ${total} Blöcken offen.`;
  const showSoftPflicht = canAccess && deepeningAnsweredCount < 6;
  return (
    <motion.div
      whileHover={canAccess ? { y: -4, boxShadow: '0 12px 40px rgba(91,184,196,0.18)' } : {}}
      onClick={canAccess ? onClick : undefined}
      className="p-8 text-center"
      style={{
        background: 'var(--bg-surface)',
        border: `1px solid ${canAccess ? 'var(--accent-green)' : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-lg)',
        backdropFilter: 'var(--glass-blur)',
        boxShadow: 'var(--card-shadow)',
        opacity: canAccess ? 1 : 0.55,
        cursor: canAccess ? 'pointer' : 'not-allowed'
      }}
      data-testid="projection-card"
    >
      <div className="hex-clip mx-auto flex items-center justify-center mb-4" style={{ width: 56, height: 56, background: canAccess ? 'var(--accent-green)' : '#CBD5E1' }}>
        <Target size={28} weight={canAccess ? 'fill' : 'duotone'} style={{ color: canAccess ? '#fff' : 'var(--text-secondary)' }} />
      </div>
      <h3 className="text-xl font-bold mb-2">Strategische Projektion</h3>

      {!canAccess && (
        <div className="flex flex-col items-center gap-2 mt-1" data-testid="projection-progress-hint">
          <div className="flex items-center gap-1.5">
            {STAR_BLOCKS.map((b, i) => {
              const isDone = blockStatus[b.id] === 'done';
              return (
                <motion.span key={b.id}
                  initial={false}
                  animate={isDone ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  style={{
                    fontSize: 18,
                    color: isDone ? '#F59E0B' : 'var(--text-secondary)',
                    opacity: isDone ? 1 : 0.32,
                    filter: isDone ? 'drop-shadow(0 0 4px rgba(245,158,11,0.55))' : 'none',
                    fontWeight: isDone ? 700 : 400,
                    letterSpacing: '0.05em',
                    lineHeight: 1,
                  }}>
                  {isDone ? '✦' : '✧'}
                </motion.span>
              );
            })}
          </div>
          <p className="text-xs" style={{ color: 'var(--text-secondary)', letterSpacing: '0.02em' }}>
            {hintText}
          </p>
        </div>
      )}

      {canAccess && (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Alle Blöcke abgeschlossen. Finale Analyse verfügbar.
        </p>
      )}
      {showSoftPflicht && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-3 px-4 py-2 inline-flex items-center gap-2 text-xs font-medium"
          style={{
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: '9999px',
            color: '#92400E',
          }}
          data-testid="projection-soft-pflicht">
          ✦ Du hast {deepeningAnsweredCount} von 6 KI-Vertiefungen genutzt — mehr Antworten = tieferes Dossier
        </motion.div>
      )}
      {canAccess && (
        <motion.div className="mt-4 flex items-center justify-center gap-2 font-medium" style={{ color: 'var(--accent-cyan)' }}
          animate={{ x: [0, 5, 0] }} transition={{ duration: 1, repeat: Infinity }}>
          <span className="text-sm">Finale ansehen</span> <ArrowRight size={16} />
        </motion.div>
      )}
    </motion.div>
  );
};


// ==================== DUAL PATH SECTION (Dashboard) ====================
// DualPathSection is now imported from ./components/DualPathSection.jsx


const DashboardPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalidSession, setInvalidSession] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showPaths, setShowPaths] = useState(false);
  const [showFeld1, setShowFeld1] = useState(false);
  const [activeDeepeningBlock, setActiveDeepeningBlock] = useState(null);
  const [skippedThisSession, setSkippedThisSession] = useState({}); // {block_id: true} so we don't re-prompt this session

  const fetchStatus = useCallback(async () => {
    try {
      if (!sessionId) { setInvalidSession(true); setLoading(false); return; }
      const response = await axios.get(`${API}/user/status/${sessionId}`);
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching status:', err);
      setInvalidSession(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-open the deepening modal when a block is done but its deepening is still pending
  useEffect(() => {
    if (!status || activeDeepeningBlock) return;
    const order = ['hidden_talent', 'geist', 'koerper', 'wesen', 'biografie', 'kompass'];
    const next = order.find(b =>
      status.deepening_status?.[b] === 'pending' && !skippedThisSession[b]
    );
    if (next) {
      const t = setTimeout(() => setActiveDeepeningBlock(next), 700);
      return () => clearTimeout(t);
    }
  }, [status, activeDeepeningBlock, skippedThisSession]);

  const handleDeepeningClose = (result) => {
    const closedBlock = activeDeepeningBlock;
    setActiveDeepeningBlock(null);
    if (result === null || result === 'skipped') {
      // User skipped or closed without answering — don't re-prompt this session
      setSkippedThisSession(prev => ({ ...prev, [closedBlock]: true }));
    }
    // Refresh status to reflect new deepening_answered_count
    fetchStatus();
  };

  const handleDevUnlock = async () => {
    setUnlocking(true);
    try {
      await axios.post(`${API}/dev/unlock-all`, { session_id: sessionId });
      await fetchStatus();
    } catch (err) {
      console.error('Unlock error:', err);
    } finally {
      setUnlocking(false);
    }
  };

  // Guard: redirect to landing if session is invalid
  useEffect(() => {
    if (invalidSession && !loading) {
      navigate('/', { state: { showResume: true } });
    }
  }, [invalidSession, loading, navigate]);

  const handleBlockClick = async (blockId, blockStatus) => {
    if (blockId === 'hidden_talent') {
      if (blockStatus === 'done') {
        // Check if instinct profile exists and is done
        try {
          const res = await axios.get(`${API}/user/instinct-profile/${sessionId}`);
          if (res.data?.status === 'done') {
            navigate(`/instinct-profile/${sessionId}`);
            return;
          }
        } catch {}
        // If instinct profile not done, go back to hidden talent scan
        navigate(`/hidden-talent/${sessionId}`);
      } else {
        navigate(`/hidden-talent/${sessionId}`);
      }
    } else if (blockId === 'geist') {
      navigate(`/deep-scan/${sessionId}`);
    } else if (blockId === 'koerper') {
      navigate(`/physical-assessment/${sessionId}`);
    } else if (blockId === 'wesen') {
      navigate(`/character-assessment/${sessionId}`);
    } else if (blockId === 'biografie') {
      navigate(`/biography/${sessionId}`);
    } else if (blockId === 'kompass') {
      navigate(`/kompass/${sessionId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Fingerprint size={64} className="mx-auto mb-4 animate-pulse" />
          <p className="text-mono" style={{ color: 'var(--text-secondary)' }}>LADE DASHBOARD...</p>
        </motion.div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <p className="text-mono" style={{ color: 'var(--accent-cyan)' }}>Status nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="page-shell-bg" style={{ alignItems: 'flex-start', paddingTop: '2rem' }}>
      <DashboardProgressBar percent={status.progress_percent} blockStatus={status.block_status} />
      
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-4xl page-shell-card relative">
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Fingerprint size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">DEEPDIVE</h2>
              <p className="text-xs" style={{ color: 'var(--accent-cyan)' }}>Subject #{status.subject_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>

        <div className="page-shell-body">

        {/* Mobile-only progress indicator (in-flow, avoids the floating top-right collision) */}
        <div className="sm:hidden flex items-center justify-between gap-2 mb-6" data-testid="dashboard-progress-mobile">
          <span className="text-xs font-medium" style={{ color: 'var(--accent-cyan)' }}>
            {status.progress_percent}% abgeschlossen
          </span>
          <div className="flex items-center gap-1.5">
            {STAR_BLOCKS.map((b) => {
              const done = status.block_status?.[b.id] === 'done';
              return (
                <Star key={b.id} size={16} weight={done ? 'fill' : 'duotone'}
                  color={done ? '#F59E0B' : 'var(--text-secondary)'} />
              );
            })}
          </div>
        </div>

        {/* Connection Line */}
        <div className="flex justify-center mb-8">
          <div className="w-px h-12" style={{ background: 'var(--border-default)' }} />
        </div>

        {/* ========== FELD 1: IDENTITAET — Kachel oder Overlay ========== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          {status.feld1_sealed && status.feld1 ? (
            /* Sealed State: Certificate-like card */
            <motion.div
              className="p-5 cursor-pointer"
              style={{ background: 'var(--bg-surface)', border: '2px solid var(--text-primary)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--card-shadow)' }}
              onClick={() => setShowFeld1(true)}
              whileHover={{ y: -2, boxShadow: '0 12px 40px rgba(45,138,110,0.14)' }}
              data-testid="feld1-sealed-card"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-4">
                  <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--text-primary)' }}>
                    <Fingerprint size={20} weight="fill" color="#fff" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-bold text-lg">{status.feld1.name}</span>
                      <span className="text-xs px-2.5 py-1 font-medium" style={{ background: '#F1F5F9', color: 'var(--text-secondary)', borderRadius: '9999px' }}>
                        {status.feld1.alter} J. | BMI {status.feld1.bmi}
                      </span>
                      {status.life_stage && (
                        <span className="text-xs px-2.5 py-1 font-semibold" style={{ background: 'rgba(91,184,196,0.12)', color: 'var(--accent-cyan)', borderRadius: '9999px', letterSpacing: '0.05em' }} data-testid="dashboard-life-stage">
                          {status.life_stage.name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                      {status.feld1.aktueller_job}
                      {status.feld1.leidenschaft_hobby && (
                        <span style={{ color: 'var(--accent-gold)' }}> · {status.feld1.leidenschaft_hobby} · {status.feld1.intensitaet}/10 ({status.feld1.intensitaet_label})</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-3 py-1.5 font-semibold" style={{ background: 'var(--accent-cyan)', color: '#fff', borderRadius: '9999px' }}>
                    {status.feld1.erwartung?.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs font-semibold" style={{ color: 'var(--accent-green)', letterSpacing: '0.1em' }}>Versiegelt</span>
                </div>
              </div>
            </motion.div>
          ) : (
            /* Open State: Clickable to open overlay */
            <motion.div
              whileHover={{ y: -2, boxShadow: '0 12px 40px rgba(91,184,196,0.18)' }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setShowFeld1(true)}
              className="p-6 cursor-pointer relative overflow-hidden"
              style={{ background: 'var(--bg-surface)', border: '2.5px solid #B91C1C', borderRadius: 'var(--radius-md)', boxShadow: 'var(--card-shadow)' }}
              data-testid="feld1-open-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                    <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'rgba(91,184,196,0.12)' }}>
                      <Fingerprint size={20} style={{ color: 'var(--accent-cyan)' }} />
                    </div>
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-bold">Identität</h3>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Stammdaten erfassen und versiegeln</p>
                  </div>
                </div>
                <motion.div animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold"
                  style={{ background: 'var(--accent-cyan)', color: '#fff', borderRadius: '9999px' }}>
                  Starten <ArrowRight size={16} />
                </motion.div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Connection Line */}
        <div className="flex justify-center mb-6">
          <div className="w-px h-8" style={{ background: status.feld1_sealed ? 'var(--accent-green)' : 'var(--border-default)' }} />
        </div>

        {/* ========== BLOCK 0: HIDDEN TALENT SCAN ========== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
          <motion.div
            whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(229,165,10,0.14)' }}
            whileTap={{ scale: 0.99 }}
            onClick={() => handleBlockClick('hidden_talent', status.block_status?.hidden_talent)}
            className="relative p-6 cursor-pointer overflow-hidden"
            style={{
              background: 'var(--bg-surface)',
              border: `${status.block_status?.hidden_talent === 'done' ? '2px solid var(--text-primary)' : '2.5px solid #B91C1C'}`,
              borderRadius: 'var(--radius-md)', backdropFilter: 'var(--glass-blur)', boxShadow: 'var(--card-shadow)'
            }}
            data-testid="block-card-hidden_talent"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="hex-clip flex items-center justify-center" style={{ width: 48, height: 48, background: status.block_status?.hidden_talent === 'done' ? 'var(--text-primary)' : 'rgba(185,28,28,0.1)' }}>
                    <Lightning size={24} weight={status.block_status?.hidden_talent === 'done' ? 'fill' : 'duotone'} style={{ color: status.block_status?.hidden_talent === 'done' ? '#fff' : '#B91C1C' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Hidden Talent Scan</h3>
                    <p className="text-xs font-semibold" style={{ color: status.block_status?.hidden_talent === 'done' ? 'var(--text-primary)' : '#B91C1C', letterSpacing: '0.08em' }}>THE RAW TRUTH</p>
                  </div>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  11 Quick-Fire-Fragen. Deine unkonditionierten Instinkte, bevor die Gesellschaft dich geformt hat.
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                {status.block_status?.hidden_talent === 'done' ? (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 px-3 py-1 text-xs font-semibold" style={{ background: 'var(--text-primary)', color: 'var(--bg-void)', borderRadius: '9999px' }}>
                    <Check size={14} weight="bold" /> Fertig
                  </motion.div>
                ) : (
                  <motion.div animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold"
                    style={{ background: '#B91C1C', color: '#fff', borderRadius: '9999px' }}>
                    Starten <ArrowRight size={16} />
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Connection Line */}
        <div className="flex justify-center mb-6">
          <div className="w-px h-8" style={{ background: status.block_status?.hidden_talent === 'done' ? 'var(--accent-gold)' : 'var(--border-default)' }} />
        </div>

        {/* ========== BLOCKS 1-5 + PHASEN-SCAN: 2×3 Grid ========== */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="grid grid-cols-1 md:grid-cols-2 auto-rows-fr gap-6 mb-8">
          {Object.values(BLOCKS).map((block, index) => (
            <motion.div key={block.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * (index + 1) }} className="h-full">
              <BlockCard block={block} status={status.block_status[block.id] || 'active'} onClick={() => handleBlockClick(block.id)} />
            </motion.div>
          ))}

          {/* Phasen-Scan als 6. Kachel im Grid */}
          {(() => {
            const isUnlocked = status.feld1_sealed && status.life_stage;
            const isDone = status.phase_scan_completed;
            const stageName = status.life_stage?.name?.toUpperCase() || 'NACH FELD 1';
            return (
              <motion.div key="phasen-scan-cell" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * (Object.values(BLOCKS).length + 1) }} className="h-full">
                <motion.div
                  whileHover={isUnlocked ? { y: -4, boxShadow: '0 12px 40px rgba(91,184,196,0.18)' } : {}}
                  whileTap={isUnlocked ? { scale: 0.98 } : {}}
                  onClick={isUnlocked ? () => navigate(`/phasen-scan/${sessionId}`) : undefined}
                  className="relative p-6 h-full"
                  style={{
                    background: 'var(--bg-surface)',
                    border: isDone
                      ? '2px solid var(--text-primary)'
                      : isUnlocked
                        ? '2.5px solid var(--accent-cyan)'
                        : '2.5px solid #B91C1C',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--card-shadow)',
                    minHeight: '200px',
                    opacity: isUnlocked ? 1 : 0.6,
                    cursor: isUnlocked ? 'pointer' : 'not-allowed',
                  }}
                  data-testid="phasen-scan-card">
                  <div className="absolute top-4 right-4">
                    {isDone ? (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-semibold"
                        style={{ background: 'var(--text-primary)', color: 'var(--bg-void)', borderRadius: '9999px' }}>
                        <Check size={14} weight="bold" /> Fertig
                      </motion.div>
                    ) : isUnlocked ? (
                      <span className="px-3 py-1 text-xs font-semibold"
                        style={{ background: 'rgba(91,184,196,0.15)', color: 'var(--accent-cyan)', borderRadius: '9999px' }}>
                        Phase aktiv
                      </span>
                    ) : (
                      <span className="px-3 py-1 text-xs font-semibold flex items-center gap-1"
                        style={{ background: 'rgba(185,28,28,0.1)', color: '#B91C1C', borderRadius: '9999px' }}>
                        <Lock size={11} weight="bold" /> Nach Feld 1
                      </span>
                    )}
                  </div>
                  <div className="mb-4">
                    <div className="hex-clip inline-flex items-center justify-center"
                      style={{ width: 48, height: 48, background: isDone ? 'var(--text-primary)' : isUnlocked ? 'rgba(91,184,196,0.15)' : 'rgba(185,28,28,0.1)' }}>
                      <Fingerprint size={24} weight={isDone ? 'fill' : 'duotone'}
                        style={{ color: isDone ? '#fff' : isUnlocked ? 'var(--accent-cyan)' : '#B91C1C' }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>PHASEN-SCAN</h3>
                  {isUnlocked && (
                    <p className="text-xs font-semibold mb-3"
                      style={{ color: isDone ? 'var(--text-primary)' : 'var(--accent-cyan)', letterSpacing: '0.08em' }}>
                      {`${stageName} · LEBENSPHASE`}
                    </p>
                  )}
                  {!isUnlocked && (
                    <p className="text-xs font-semibold mb-3"
                      style={{ color: '#B91C1C', letterSpacing: '0.08em' }}>
                      LEBENSPHASEN-MODUL
                    </p>
                  )}
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    12 Fragen + 1 strategische Aufgabe deiner aktuellen Lebensphase
                  </p>
                  {isUnlocked && !isDone && (
                    <div className="absolute bottom-4 right-4 flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--accent-cyan)' }}>
                      Starten <ArrowRight size={16} />
                    </div>
                  )}
                </motion.div>
              </motion.div>
            );
          })()}
        </motion.div>

        {/* Connection to Projection */}
        <div className="flex justify-center mb-8">
          <div className="w-px h-12" style={{ background: status.can_access_projection ? 'var(--accent-green)' : 'var(--border-default)' }} />
        </div>

        {/* Strategic Projection */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-8">
          <ProjectionCard canAccess={status.can_access_projection} blockStatus={status.block_status} deepeningAnsweredCount={status.deepening_answered_count || 0} onClick={() => {
            if (status.projection_generated) {
              navigate(`/projection/${sessionId}`);
            } else if (!status.payment_completed) {
              navigate(`/checkout/${sessionId}`);
            } else {
              navigate(`/strategic-projection/${sessionId}`);
            }
          }} />
        </motion.div>

        {/* Footer */}
        <footer className="mt-16 text-center space-y-4">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Session: {sessionId.substring(0, 8)}... · Altersgruppe: {status.age_group} · Phase: {status.current_phase}/5
          </p>
          {!IS_PRODUCTION && status.progress_percent < 100 && (
            <button onClick={handleDevUnlock} disabled={unlocking}
              className="text-xs px-4 py-2 transition-all"
              style={{ border: '1px solid var(--border-default)', color: 'var(--text-secondary)', background: 'transparent', borderRadius: '9999px', cursor: 'pointer', opacity: unlocking ? 0.5 : 0.5 }}
              data-testid="dev-unlock-btn">
              {unlocking ? 'Wird freigeschaltet...' : 'DEV: Alle Bloecke freischalten'}
            </button>
          )}
        </footer>

        {/* Feld 1 Overlay */}
        </div>{/* end page-shell-body */}
        {showFeld1 && !status.feld1_sealed && (
          <Feld1Overlay
            sessionId={sessionId}
            onSealed={(feld1Data) => {
              setShowFeld1(false);
              fetchStatus();
            }}
            onClose={() => setShowFeld1(false)}
          />
        )}

        {/* KI-Vertiefungsfrage Modal — öffnet automatisch nach jedem abgeschlossenen Block */}
        {activeDeepeningBlock && (
          <DeepeningQuestion
            sessionId={sessionId}
            blockId={activeDeepeningBlock}
            onClose={handleDeepeningClose}
          />
        )}
      </motion.div>
    </div>
  );
};

// ==================== RESUME PAGE ====================

const ResumePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [debugLink, setDebugLink] = useState(null);

  // Check for token in URL on mount
  const verifyToken = useCallback(async (token) => {
    setVerifying(true);
    try {
      const response = await axios.get(`${API}/auth/verify-token?token=${token}`);
      if (response.data.valid) {
        // Redirect to dashboard
        navigate(`/dashboard/${response.data.session_id}`);
      } else {
        setError(response.data.message);
        setVerifying(false);
      }
    } catch (err) {
      setError('Token-Verifizierung fehlgeschlagen');
      setVerifying(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyToken(token);
    }
  }, [searchParams, verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    setDebugLink(null);

    try {
      const response = await axios.post(`${API}/auth/request-access`, {
        identifier: identifier.trim()
      });
      
      setSuccess(true);
      // For testing: show the debug link
      if (response.data._debug_link) {
        setDebugLink(response.data._debug_link);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  // Token verification loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <Fingerprint size={64} className="mx-auto mb-4" />
          </motion.div>
          <p className="text-mono" style={{ color: 'var(--text-secondary)' }}>VERIFIZIERE ZUGANG...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
      <NoiseOverlay />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <Fingerprint size={48} className="mx-auto mb-4" />
          <h1 className="heading-large mb-2">ZUGANG FORTSETZEN</h1>
          <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
            Kehre zu deinem Assessment zurück
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="p-6 mb-6" style={{ background: 'var(--bg-surface)', border: '1px solid #22C55E' }}>
              <Check size={48} className="mx-auto mb-4" style={{ color: '#22C55E' }} />
              <p className="text-mono mb-2" style={{ color: '#22C55E' }}>
                ZUGANGSSCHLÜSSEL GENERIERT
              </p>
              <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                Ein verschlüsselter Zugangsschlüssel wurde erstellt.
                {debugLink && ' (Test-Modus: Link unten)'}
              </p>
            </div>

            {/* Debug Link for Testing */}
            {debugLink && (
              <div className="p-4 mb-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
                <p className="text-overline mb-2">TEST-MODUS: MAGIC LINK</p>
                <p 
                  className="text-mono text-sm break-all mb-4"
                  style={{ color: 'var(--accent-blue)' }}
                >
                  Token bereit zur Verifizierung
                </p>
                <button
                  onClick={() => {
                    // Extract token and use current origin
                    const token = debugLink.split('token=')[1];
                    window.location.href = `/resume?token=${token}`;
                  }}
                  className="btn-brutal btn-brutal-filled w-full"
                >
                  ZUM DASHBOARD
                </button>
              </div>
            )}

            <button
              onClick={() => { setSuccess(false); setIdentifier(''); }}
              className="btn-brutal"
            >
              ANDEREN ACCOUNT VERWENDEN
            </button>
          </motion.div>
        ) : (
          /* Input Form */
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="text-overline block mb-3">
                IDENTITÄT BESTÄTIGEN
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="E-MAIL ODER SUBJECT-ID EINGEBEN"
                className="input-brutal text-center uppercase"
                style={{ letterSpacing: '0.1em' }}
                data-testid="resume-input"
              />
              <p className="text-mono text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                Deine Subject-ID findest du oben auf deinem Dashboard (z.B. #325CAC48)
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 mb-6 flex items-center gap-3"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--accent-cyan)' }}
              >
                <Warning size={24} style={{ color: 'var(--accent-cyan)' }} />
                <p className="text-mono text-sm" style={{ color: 'var(--accent-cyan)' }}>{error}</p>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading || !identifier.trim()}
              className="btn-brutal btn-brutal-filled w-full flex items-center justify-center gap-3"
              data-testid="resume-submit"
            >
              {loading ? (
                <>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                    <Fingerprint size={20} />
                  </motion.div>
                  WIRD GEPRÜFT...
                </>
              ) : (
                <>
                  ZUGANG ANFORDERN
                  <ArrowRight size={20} weight="bold" />
                </>
              )}
            </button>
          </form>
        )}

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/')}
            className="text-mono text-sm"
            style={{ color: 'var(--text-secondary)' }}
          >
            ← NEUES ASSESSMENT STARTEN
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ==================== APP ====================

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/resume" element={<ResumePage />} />
          <Route path="/dashboard/:sessionId" element={<DashboardPage />} />
          <Route path="/hidden-talent/:sessionId" element={<HiddenTalentScan />} />
          <Route path="/instinct-profile/:sessionId" element={<InstinctProfile />} />
          <Route path="/quick-scan/:sessionId" element={<QuickScanPage />} />
          <Route path="/checkout/:sessionId" element={<CheckoutPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/deep-scan/:sessionId" element={<DeepScanPage />} />
          <Route path="/physical-assessment/:sessionId" element={<PhysicalAssessment />} />
          <Route path="/character-assessment/:sessionId" element={<CharacterAssessment />} />
          <Route path="/phasen-scan/:sessionId" element={<PhasenScan />} />
          <Route path="/kompass/:sessionId" element={<KompassAssessment />} />
          <Route path="/biography/:sessionId" element={<BiographicalDeepDive />} />
          <Route path="/strategic-projection/:sessionId" element={<StrategicProjection />} />
          <Route path="/projection/:sessionId" element={<ProjectionPage />} />
          <Route path="/dossier/:sessionId" element={<DossierPage />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
